/**
 * GDSCardTutorial.js
 * Manages the introductory tutorial overlay for the card selection step
 */
export class GDSCardTutorial {
    constructor(builder) {
        this.builder = builder;
        this.cookieName = 'gds_card_tutorial_shown';
        this.cookieExpireDays = 60;
        this.tutorialActive = false;
    }

    /**
     * Checks if tutorial should be shown and displays it if needed
     */
    async checkAndShowTutorial() {
        // Check if tutorial has been shown before
        if (this.getTutorialCookie()) {
            return;
        }

        // Wait for cards to be fully rendered
        await this.waitForCards();

        // Find a suitable card to highlight
        const targetCard = this.findSuitableCard();
        if (!targetCard) {
            console.warn('[GDSCardTutorial] No suitable card found for tutorial - skipping');
            // Set cookie anyway to prevent repeated attempts
            this.setTutorialCookie();
            return;
        }

        // Show the tutorial
        this.showTutorial(targetCard);
    }

    /**
     * Waits for cards to be rendered in the DOM
     */
    async waitForCards() {
        const maxAttempts = 20;
        let attempts = 0;

        while (attempts < maxAttempts) {
            const cards = document.querySelectorAll('.product-card:not(.unavailable-product)');
            if (cards.length > 0) {
                // Give UI time to settle
                await new Promise(resolve => setTimeout(resolve, 300));
                return;
            }
            await new Promise(resolve => setTimeout(resolve, 100));
            attempts++;
        }
    }

    /**
     * Finds a suitable card to use for the tutorial
     * MUST be available AND have an edit button
     */
    findSuitableCard() {
        // Find all available cards (not unavailable)
        const availableCards = document.querySelectorAll('.product-card[data-variant-available="true"]');

        // Find the first available card that has an edit button
        for (const card of availableCards) {
            if (card.querySelector('.edit-options-btn')) {
                return card;
            }
        }

        // No suitable card found
        console.warn('[GDSCardTutorial] No available card with edit button found');
        return null;
    }

    /**
     * Creates and displays the tutorial overlay
     */
    showTutorial(targetCard) {
        this.tutorialActive = true;

        // Create backdrop
        const backdrop = document.createElement('div');
        backdrop.className = 'gds-tutorial-backdrop';
        backdrop.innerHTML = this.createTutorialHTML(targetCard);
        document.body.appendChild(backdrop);

        // Position the highlighted card
        this.positionHighlightedCard(targetCard, backdrop);

        // Setup event listeners
        this.setupTutorialListeners(backdrop);

        // Prevent body scroll
        document.body.style.overflow = 'hidden';
    }

    /**
     * Creates the HTML for the tutorial overlay
     */
    createTutorialHTML(targetCard) {
        const isMobile = window.innerWidth < 768;

        if (isMobile) {
            return this.createMobileTutorialHTML();
        } else {
            return this.createDesktopTutorialHTML();
        }
    }

    /**
     * Creates mobile-specific tutorial HTML with card-styled annotations
     */
    createMobileTutorialHTML() {
        return `
            <div class="gds-tutorial-overlay gds-tutorial-mobile">
                <!-- Close button -->
                <button class="gds-tutorial-close" aria-label="Close tutorial">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M18 6L6 18M6 6l12 12"/>
                    </svg>
                </button>

                <!-- Skip button -->
                <button class="gds-tutorial-skip">Skip Tutorial</button>

                <!-- Mobile tutorial container -->
                <div class="gds-tutorial-mobile-container">
                    <!-- Select annotation card -->
                    <div class="gds-tutorial-annotation-card">
                        <div class="block">
                            <div class="flex flex-row">
                                <div class="w-1/3 p-3 flex items-center justify-center">
                                    <div class="h-12 w-12 bg-[#00B588] rounded-full flex items-center justify-center">
                                        <svg class="h-8 w-8 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                            <path d="M5 13l4 4L19 7" stroke-linecap="round" stroke-linejoin="round"></path>
                                        </svg>
                                    </div>
                                </div>
                                <div class="w-3/5 p-3 flex flex-col justify-center">
                                    <h3 class="text-base font-semibold text-gray-900">Select Products</h3>
                                    <p class="text-sm text-gray-600 mt-1">Tap cards to add or remove them from your order</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Edit annotation card -->
                    <div class="gds-tutorial-annotation-card">
                        <div class="block">
                            <div class="flex flex-row">
                                <div class="w-1/3 p-3 flex items-center justify-center">
                                    <svg class="w-12 h-12 text-gray-600" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
                                        <g>
                                            <rect width="24" height="24" fill="none"></rect>
                                            <g>
                                                <path d="M0,2A2,2,0,0,1,2,0H4A2,2,0,0,1,6,2V18L3,23,0,18Z" transform="translate(18.01 2.247) rotate(45)" fill="none" stroke="currentColor" stroke-miterlimit="10" stroke-width="1.2"></path>
                                                <path d="M0,1H5" transform="translate(15.536 5.429) rotate(45)" fill="none" stroke="currentColor" stroke-linecap="square" stroke-miterlimit="10" stroke-width="1.2"></path>
                                            </g>
                                        </g>
                                    </svg>
                                </div>
                                <div class="w-3/5 p-3 flex flex-col justify-center">
                                    <h3 class="text-base font-semibold text-gray-900">Customize Options</h3>
                                    <p class="text-sm text-gray-600 mt-1">Tap to change colors and other options</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Preview annotation card -->
                    <div class="gds-tutorial-annotation-card">
                        <div class="block">
                            <div class="flex flex-row">
                                <div class="w-1/3 p-3 flex items-center justify-center">
                                    <div class="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                                        <svg class="w-10 h-10 text-gray-600" viewBox="0 0 512 512" fill="currentColor">
                                            <path d="M503.698,231.895c-28.735-36.843-65.956-67.318-107.637-88.128c-42.548-21.243-88.321-32.265-136.104-32.843 c-1.316-0.036-6.6-0.036-7.916,0c-47.782,0.579-93.556,11.6-136.104,32.843c-41.681,20.81-78.9,51.284-107.636,88.128 c-11.07,14.193-11.07,34.018,0,48.211c28.735,36.843,65.955,67.318,107.636,88.128c42.548,21.243,88.321,32.265,136.104,32.843 c1.316,0.036,6.6,0.036,7.916,0c47.782-0.579,93.556-11.6,136.104-32.843c41.681-20.81,78.901-51.284,107.637-88.128 C514.768,265.911,514.768,246.088,503.698,231.895z M256,380.303c-68.542,0-124.304-55.762-124.304-124.304 S187.458,131.696,256,131.696S380.304,187.458,380.304,256S324.542,380.303,256,380.303z"/>
                                            <path d="M256,202.804c-29.332,0-53.195,23.863-53.195,53.195s23.863,53.195,53.195,53.195s53.195-23.863,53.195-53.195 C309.196,226.667,285.333,202.804,256,202.804z M256,288.367c-17.847,0-32.368-14.519-32.368-32.368 c0-17.848,14.519-32.368,32.368-32.368c17.847,0,32.367,14.519,32.367,32.367C288.368,273.848,273.847,288.367,256,288.367z"/>
                                        </svg>
                                    </div>
                                </div>
                                <div class="w-3/5 p-3 flex flex-col justify-center">
                                    <h3 class="text-base font-semibold text-gray-900">Preview Details</h3>
                                    <p class="text-sm text-gray-600 mt-1">Tap the eye icon to view additional product details</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Continue button -->
                <button class="gds-tutorial-continue">Got it!</button>
            </div>

            ${this.getTutorialStyles()}
        `;
    }

    /**
     * Creates desktop-specific tutorial HTML (original version)
     */
    createDesktopTutorialHTML() {
        return `
            <div class="gds-tutorial-overlay">
                <!-- Close button -->
                <button class="gds-tutorial-close" aria-label="Close tutorial">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M18 6L6 18M6 6l12 12"/>
                    </svg>
                </button>

                <!-- Skip button -->
                <button class="gds-tutorial-skip">Skip Tutorial</button>

                <!-- Highlighted card container -->
                <div class="gds-tutorial-card-container"></div>

                <!-- Arrows and labels -->
                <div class="gds-tutorial-annotations">
                  <!-- Select checkbox annotation -->
                  <div class="gds-tutorial-annotation gds-annotation-select">
                      <div class="gds-tutorial-arrow gds-arrow-select"></div>
                      <div class="gds-tutorial-label">
                        <div class="flex items-center justify-between mb-2">
                          <strong>Select Products</strong>
                          <div class="h-6 w-6 bg-[#00B588] rounded-full flex items-center justify-center shadow-md">
                              <svg class="h-5 w-5 text-white checkmark-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="display: block">
                                  <path d="M5 13l4 4L19 7" stroke-linecap="round" stroke-linejoin="round"></path>
                              </svg>
                          </div>
                        </div>
                          <p>Click cards to add or remove them from your order</p>
                      </div>
                  </div>

                    <!-- Edit button annotation (positioned above preview) -->
                    <div class="gds-tutorial-annotation gds-annotation-edit">
                        <div class="gds-tutorial-label">
                            <div class="flex items-center justify-between mb-2">
                                <strong>Customize Options</strong>
                                <svg class="w-6 h-6 text-gray-600" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
                                    <g>
                                        <defs><clipPath><rect width="24" height="24" fill="none"></rect></clipPath></defs>
                                        <rect width="24" height="24" fill="none"></rect>
                                        <g>
                                            <path d="M0,2A2,2,0,0,1,2,0H4A2,2,0,0,1,6,2V18L3,23,0,18Z" transform="translate(18.01 2.247) rotate(45)" fill="none" stroke="currentColor" stroke-miterlimit="10" stroke-width="1.2"></path>
                                            <path d="M0,1H5" transform="translate(15.536 5.429) rotate(45)" fill="none" stroke="currentColor" stroke-linecap="square" stroke-miterlimit="10" stroke-width="1.2"></path>
                                        </g>
                                    </g>
                                </svg>
                            </div>
                            <p>Click to change colors and other options</p>
                        </div>
                        <div class="gds-tutorial-arrow gds-arrow-edit"></div>
                    </div>

                    <!-- Preview button annotation -->
                    <div class="gds-tutorial-annotation gds-annotation-preview">
                        <div class="gds-tutorial-label">
                          <div class="flex items-center justify-between mb-2">
                              <strong>Preview Details</strong>
                              <div class="w-6 h-6 bg-white backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform duration-300 pointer-events-none">
                                  <svg class="w-8 h-8 text-gray-600 hover:text-gray-800 transition-colors duration-200 pointer-events-none" viewBox="0 0 512 512" fill="currentColor">
                                      <path d="M503.698,231.895c-28.735-36.843-65.956-67.318-107.637-88.128c-42.548-21.243-88.321-32.265-136.104-32.843 c-1.316-0.036-6.6-0.036-7.916,0c-47.782,0.579-93.556,11.6-136.104,32.843c-41.681,20.81-78.9,51.284-107.636,88.128 c-11.07,14.193-11.07,34.018,0,48.211c28.735,36.843,65.955,67.318,107.636,88.128c42.548,21.243,88.321,32.265,136.104,32.843 c1.316,0.036,6.6,0.036,7.916,0c47.782-0.579,93.556-11.6,136.104-32.843c41.681-20.81,78.901-51.284,107.637-88.128 C514.768,265.911,514.768,246.088,503.698,231.895z M256,380.303c-68.542,0-124.304-55.762-124.304-124.304 S187.458,131.696,256,131.696S380.304,187.458,380.304,256S324.542,380.303,256,380.303z"/>
                                      <path d="M256,202.804c-29.332,0-53.195,23.863-53.195,53.195s23.863,53.195,53.195,53.195s53.195-23.863,53.195-53.195 C309.196,226.667,285.333,202.804,256,202.804z M256,288.367c-17.847,0-32.368-14.519-32.368-32.368 c0-17.848,14.519-32.368,32.368-32.368c17.847,0,32.367,14.519,32.367,32.367C288.368,273.848,273.847,288.367,256,288.367z"/>
                                  </svg>
                              </div>
                          </div>
                          <p>You can hover over a product card and click the eye icon to view additional product details.</p>
                        </div>
                        <div class="gds-tutorial-arrow gds-arrow-preview"></div>
                    </div>
                </div>

                <!-- Continue button -->
                <button class="gds-tutorial-continue">Got it!</button>
            </div>

            ${this.getTutorialStyles()}
        `;
    }

    /**
     * Returns the CSS styles for the tutorial
     */
    getTutorialStyles() {
        return `
            <style>
                .gds-tutorial-backdrop {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.9);
                    z-index: 9999;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .gds-tutorial-overlay {
                    position: relative;
                    width: 100%;
                    height: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .gds-tutorial-close {
                    position: absolute;
                    top: 20px;
                    right: 20px;
                    background: rgba(255, 255, 255, 0.1);
                    border: 1px solid rgba(255, 255, 255, 0.3);
                    color: white;
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: all 0.2s;
                    z-index: 100;
                }

                .gds-tutorial-close:hover {
                    background: rgba(255, 255, 255, 0.2);
                }

                .gds-tutorial-skip {
                    position: absolute;
                    top: 30px;
                    left: 30px;
                    background: transparent;
                    border: 1px solid rgba(255, 255, 255, 0.5);
                    color: white;
                    padding: 8px 16px;
                    border-radius: 20px;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .gds-tutorial-skip:hover {
                    background: rgba(255, 255, 255, 0.1);
                    border-color: white;
                }

                /* Mobile-specific styles */
                .gds-tutorial-mobile .gds-tutorial-mobile-container {
                    display: flex;
                    flex-direction: column;
                    gap: 20px;
                    padding: 20px;
                    width: 100%;
                    max-width: 360px;
                    margin: 0 auto;
                    align-items: center;
                }

                .gds-tutorial-annotation-card {
                    background: rgba(255, 255, 255, 0.95);
                    border-radius: 24px;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
                    width: 100%;
                    max-width: 320px;
                    animation: fadeInAnnotation 0.6s ease-out forwards;
                    opacity: 0;
                }

                .gds-tutorial-annotation-card:nth-child(1) {
                    animation-delay: 0.3s;
                }

                .gds-tutorial-annotation-card:nth-child(2) {
                    animation-delay: 0.5s;
                }

                .gds-tutorial-annotation-card:nth-child(3) {
                    animation-delay: 0.7s;
                }

                .gds-tutorial-mobile .gds-tutorial-card-container {
                    display: none !important;
                }



                /* Desktop styles remain the same */
                .gds-tutorial-card-container {
                    position: relative;
                    z-index: 10;
                }

                .gds-tutorial-card-container .product-card {
                    transform: scale(1.1);
                    transition: none !important;
                    border: 3px solid #00B588 !important;
                    box-shadow: 0 0 30px rgba(0, 181, 136, 0.3) !important;
                    pointer-events: none !important;
                    cursor: default !important;
                }

                .gds-tutorial-card-container .preview-eye-icon {
                    opacity: 1 !important;
                }

                .gds-tutorial-card-container .preview-eye-btn,
                .gds-tutorial-card-container .edit-options-btn,
                .gds-tutorial-card-container .checkmark-container {
                    pointer-events: none !important;
                }

                .gds-tutorial-annotations {
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    pointer-events: none;
                }

                .gds-tutorial-annotation {
                    position: absolute;
                    display: flex;
                    align-items: center;
                    animation: fadeInAnnotation 0.6s ease-out forwards;
                    opacity: 0;
                }

                .gds-annotation-select {
                    animation-delay: 0.3s;
                    z-index: 13;
                }

                .gds-annotation-edit {
                    animation-delay: 0.5s;
                    z-index: 12;
                    align-items: center !important;
                }

                .gds-annotation-preview {
                    animation-delay: 0.7s;
                    z-index: 11;
                }

                @keyframes fadeInAnnotation {
                    to {
                        opacity: 1;
                    }
                }

                .gds-tutorial-arrow {
                    width: 60px;
                    height: 2px;
                    background: white;
                    position: relative;
                    margin: 0 20px;
                    flex-shrink: 0;
                    align-self: center;
                }

                .gds-annotation-select .gds-tutorial-arrow {
                    width: 2px;
                    height: 40px;
                    margin: 15px 0;
                }

                .gds-tutorial-arrow::after {
                    content: '';
                    position: absolute;
                    width: 10px;
                    height: 10px;
                    border-top: 2px solid white;
                    border-right: 2px solid white;
                }

                .gds-arrow-select::after {
                    left: 50%;
                    bottom: -5px;
                    transform: translateX(-50%) rotate(135deg);
                }

                .gds-arrow-edit::after {
                    left: -2px;
                    top: -4px;
                    transform: rotate(-135deg);
                }

                .gds-arrow-preview::after {
                    right: -2px;
                    top: -4px;
                    transform: rotate(45deg);
                }

                .gds-tutorial-label {
                    background: rgba(255, 255, 255, 0.95);
                    padding: 12px 20px;
                    border-radius: 8px;
                    max-width: 250px;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
                }

                .gds-tutorial-label strong {
                    display: block;
                    color: #00B588;
                    font-size: 16px;
                    margin-bottom: 4px;
                }

                .gds-tutorial-label p {
                    color: #333;
                    font-size: 14px;
                    line-height: 1.4;
                    margin: 0;
                }

                .gds-tutorial-continue {
                    position: absolute;
                    bottom: 40px;
                    left: 50%;
                    transform: translateX(-50%);
                    background: #00B588;
                    color: white;
                    padding: 14px 40px;
                    border: none;
                    border-radius: 30px;
                    font-size: 16px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s;
                    animation: pulseButton 2s ease-in-out infinite;
                }

                @keyframes pulseButton {
                    0%, 100% {
                        transform: translateX(-50%) scale(1);
                    }
                    50% {
                        transform: translateX(-50%) scale(1.05);
                    }
                }

                .gds-tutorial-continue:hover {
                    background: #00A077;
                    transform: translateX(-50%) scale(1.05);
                }

                /* Mobile adjustments */
                @media (max-width: 768px) {
                    .gds-tutorial-mobile .gds-tutorial-skip {
                        top: 20px;
                        left: 20px;
                        padding: 6px 12px;
                        font-size: 14px;
                    }

                    .gds-tutorial-mobile .gds-tutorial-continue {
                        bottom: 20px;
                        padding: 12px 30px;
                        font-size: 14px;
                    }

                    .gds-tutorial-mobile-container {
                        max-height: calc(100vh - 140px);
                        overflow-y: auto;
                        -webkit-overflow-scrolling: touch;
                    }
                }

                /* Hide desktop elements on mobile and vice versa */
                @media (max-width: 767px) {
                    .gds-tutorial-annotations {
                        display: none !important;
                    }
                    /* Don't hide the card container on mobile! */
                    .gds-tutorial-overlay:not(.gds-tutorial-mobile) .gds-tutorial-card-container {
                        display: none !important;
                    }
                }

                @media (min-width: 768px) {
                    .gds-tutorial-mobile-container {
                        display: none !important;
                    }
                }
            </style>
        `;
    }

    /**
     * Positions the highlighted card and annotations
     */
    positionHighlightedCard(targetCard, backdrop) {
        // Skip card positioning for mobile
        if (window.innerWidth < 768) return;

        const originalRect = targetCard.getBoundingClientRect();
        const originalWidth = originalRect.width;
        const originalHeight = originalRect.height;
        console.log('originalWidth: ', originalWidth);
        console.log('originalHeight: ', originalHeight);

        const container = backdrop.querySelector('.gds-tutorial-card-container');
        if (!container) return;

        const clonedCard = targetCard.cloneNode(true);

        clonedCard.style.width = `${originalWidth}px`;
        clonedCard.style.height = `${originalHeight}px`;
        clonedCard.style.minWidth = `${originalWidth}px`;
        clonedCard.style.minHeight = `${originalHeight}px`;
        clonedCard.style.maxWidth = `${originalWidth}px`;
        clonedCard.style.maxHeight = `${originalHeight}px`;
        clonedCard.style.flexShrink = '0';
        clonedCard.style.flexGrow = '0';

        // Show the card in selected state
        clonedCard.setAttribute('data-selected', 'true');
        clonedCard.style.border = '3px solid #00B588';

        // Ensure checkmark shows selected state
        const checkmark = clonedCard.querySelector('.checkmark-container > div');
        if (checkmark) {
            checkmark.classList.remove('bg-white', 'border-2', 'border-gray');
            checkmark.classList.add('bg-[#00B588]');
            const svg = checkmark.querySelector('svg');
            if (svg) {
                svg.style.display = 'block';
                svg.style.color = 'white';
            }
        }

        // Make the preview eye visible as if hovered
        const previewEye = clonedCard.querySelector('.preview-eye-icon');
        if (previewEye) {
            previewEye.style.opacity = '1';
            previewEye.style.pointerEvents = 'none';
        }

        // Show edit button if it exists
        const editButton = clonedCard.querySelector('.edit-options-btn');
        if (editButton) {
            editButton.style.opacity = '1';
            editButton.style.visibility = 'visible';
        }

        // Disable all interactive elements
        clonedCard.style.pointerEvents = 'none';
        clonedCard.style.cursor = 'default';

        // Disable hover effects by removing event handlers
        clonedCard.removeAttribute('onmouseenter');
        clonedCard.removeAttribute('onmouseleave');

        container.appendChild(clonedCard);

        // Position annotations after a brief delay (desktop only)
        if (window.innerWidth >= 768) {
            setTimeout(() => this.positionAnnotations(backdrop, clonedCard), 100);
        }
    }

    /**
     * Positions the annotation arrows and labels (desktop only)
     */
    positionAnnotations(backdrop, card) {
        if (window.innerWidth < 768) return;

        const cardRect = card.getBoundingClientRect();

        // Position select annotation (pointing to checkbox)
        const selectAnnotation = backdrop.querySelector('.gds-annotation-select');
        const checkmark = card.querySelector('.checkmark-container');

        if (selectAnnotation && checkmark) {
            card.style.position = 'relative';
            card.offsetHeight;

            let checkmarkRect = checkmark.getBoundingClientRect();

            const checkmarkSize = 24;
            const topOffset = 8;
            const rightOffset = 8;

            const checkmarkTop = cardRect.top + topOffset;
            const checkmarkLeft = cardRect.right - rightOffset - checkmarkSize;

            selectAnnotation.style.visibility = 'hidden';
            selectAnnotation.style.display = 'flex';
            const annotationHeight = selectAnnotation.getBoundingClientRect().height;
            selectAnnotation.style.visibility = 'visible';

            const spacing = 85;

            selectAnnotation.style.top = `${checkmarkTop - annotationHeight - spacing}px`;
            selectAnnotation.style.left = `${checkmarkLeft + checkmarkSize / 2.8}px`;
            selectAnnotation.style.transform = 'translateX(-50%)';
            selectAnnotation.style.flexDirection = 'column-reverse';
            selectAnnotation.style.position = 'absolute';
            selectAnnotation.style.zIndex = '13';

            const checkmarkDiv = checkmark.querySelector('div');
            if (checkmarkDiv) {
                const checkmarkDivRect = checkmarkDiv.getBoundingClientRect();
                if (checkmarkDivRect.width > 0 && checkmarkDivRect.height > 0) {
                    selectAnnotation.style.top = `${checkmarkDivRect.top - annotationHeight - spacing}px`;
                    selectAnnotation.style.left = `${checkmarkDivRect.left + checkmarkDivRect.width / 2}px`;
                }
            }
        }

        // Position edit annotation
        const editAnnotation = backdrop.querySelector('.gds-annotation-edit');
        const editButton = card.querySelector('.edit-options-btn');
        if (editAnnotation && editButton) {
            const editRect = editButton.getBoundingClientRect();
            editAnnotation.style.top = `${cardRect.top + cardRect.height / 6}px`;
            editAnnotation.style.left = `${cardRect.right + 1}px`;
            editAnnotation.style.flexDirection = 'row-reverse';
            editAnnotation.style.transform = `translateY(-50%)`;

            const arrow = editAnnotation.querySelector('.gds-arrow-edit');
            if (arrow) {
                arrow.style.transformOrigin = 'left center';
            }
        }

        // Position preview annotation
        const previewAnnotation = backdrop.querySelector('.gds-annotation-preview');
        if (previewAnnotation) {
            previewAnnotation.style.top = `${cardRect.top + cardRect.height / 2.7}px`;
            previewAnnotation.style.right = `${window.innerWidth - cardRect.left}px`;
            previewAnnotation.style.flexDirection = 'row';
        }
    }

    /**
     * Sets up event listeners for tutorial interactions
     */
    setupTutorialListeners(backdrop) {
        const closeBtn = backdrop.querySelector('.gds-tutorial-close');
        const skipBtn = backdrop.querySelector('.gds-tutorial-skip');
        const continueBtn = backdrop.querySelector('.gds-tutorial-continue');

        const closeTutorial = () => {
            this.closeTutorial(backdrop);
        };

        closeBtn.addEventListener('click', closeTutorial);
        skipBtn.addEventListener('click', closeTutorial);
        continueBtn.addEventListener('click', closeTutorial);

        // Close on backdrop click (but not on card or annotations)
        backdrop.addEventListener('click', (e) => {
            if (e.target === backdrop || e.target.classList.contains('gds-tutorial-overlay')) {
                closeTutorial();
            }
        });

        // Close on Escape key
        this.escapeHandler = (e) => {
            if (e.key === 'Escape') {
                closeTutorial();
            }
        };
        document.addEventListener('keydown', this.escapeHandler);
    }

    /**
     * Closes the tutorial and sets the cookie
     */
    closeTutorial(backdrop) {
        this.tutorialActive = false;

        // Fade out animation
        backdrop.style.opacity = '0';
        backdrop.style.transition = 'opacity 0.3s ease-out';

        setTimeout(() => {
            backdrop.remove();
            document.body.style.overflow = '';

            // Remove escape handler
            if (this.escapeHandler) {
                document.removeEventListener('keydown', this.escapeHandler);
            }
        }, 300);

        // Set cookie to prevent showing again
        this.setTutorialCookie();
    }

    /**
     * Gets the tutorial shown cookie
     */
    getTutorialCookie() {
        const name = this.cookieName + '=';
        const decodedCookie = decodeURIComponent(document.cookie);
        const ca = decodedCookie.split(';');

        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) === ' ') {
                c = c.substring(1);
            }
            if (c.indexOf(name) === 0) {
                return c.substring(name.length, c.length);
            }
        }
        return null;
    }

    /**
     * Sets the tutorial shown cookie
     */
    setTutorialCookie() {
        const d = new Date();
        d.setTime(d.getTime() + (this.cookieExpireDays * 24 * 60 * 60 * 1000));
        const expires = 'expires=' + d.toUTCString();
        document.cookie = `${this.cookieName}=true;${expires};path=/`;
    }

    /**
     * Resets the tutorial cookie (for testing)
     */
    resetTutorial() {
        document.cookie = `${this.cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/`;
        console.log('[GDSCardTutorial] Tutorial cookie reset');
    }
}
